import {
  ApplicationRef,
  ComponentRef,
  DOCUMENT,
  EnvironmentInjector,
  Injectable,
  OutputEmitterRef,
  Type,
  createComponent,
  inject,
  signal,
} from '@angular/core';
import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { PlatformPrintService } from '@/app/data/_generated-api/platform-api/services/platform-print.service';
import { EXPECTED_API_ERRORS } from '@/app/data/_generated-api/http-interceptors/silent-api-errors';
import { downloadBlob } from '@shared/utils/download-file.utils';
import { buildPrintSnapshot } from './print-snapshot.utils';

/** Een printblad: meldt wanneer alles (SVG, data) gerenderd is, of dat het mislukte. */
export interface PrintSheet {
  readonly printReady: OutputEmitterRef<boolean>;
}

export interface PdfPrintRequest<C extends PrintSheet> {
  readonly component: Type<C>;
  readonly inputs: Readonly<Record<string, unknown>>;
  /** Bestandsnaam van de download, met extensie. */
  readonly fileName: string;
  readonly lang: string;
  readonly title: string;
}

/** `busy`: printserver bezet of rate-limit (later opnieuw); `failed`: iets anders ging mis. */
export class PdfPrintError extends Error {
  constructor(
    readonly kind: 'busy' | 'failed',
    readonly reason?: unknown,
  ) {
    super(`PDF-print mislukt (${kind})`);
  }
}

/** Ruim: de bodygraph-SVG staat na de opstart-warm-up al in de cache. */
const READY_TIMEOUT_MS = 15_000;

/**
 * Afgewerkte prints als PDF (docs/superpowers/specs/2026-10-21-chart-pdf-print-design.md): rendert
 * een printblad buiten beeld, maakt er een zelfstandige HTML-snapshot van, laat de API die met
 * headless Chrome omzetten en downloadt het resultaat. Eén print tegelijk per gebruiker.
 */
@Injectable({ providedIn: 'root' })
export class PdfPrintService {
  private readonly appRef = inject(ApplicationRef);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly document = inject(DOCUMENT);
  private readonly printApi = inject(PlatformPrintService);

  /** Er loopt een print; nieuwe verzoeken worden genegeerd tot die klaar is. */
  readonly busy = signal(false);

  /** `false` als er al een print liep (verzoek genegeerd); gooit {@link PdfPrintError} bij een fout. */
  async download<C extends PrintSheet>(request: PdfPrintRequest<C>): Promise<boolean> {
    if (this.busy()) return false;
    this.busy.set(true);
    try {
      const html = await this.renderSnapshot(request);
      const pdf = await this.renderPdf(html);
      downloadBlob(pdf, request.fileName);
      return true;
    } finally {
      this.busy.set(false);
    }
  }

  private async renderSnapshot<C extends PrintSheet>(request: PdfPrintRequest<C>): Promise<string> {
    // Buiten beeld maar wél opgemaakt: de bodygraph meet zijn SVG, display:none zou dat breken.
    const host = this.document.createElement('div');
    host.setAttribute('aria-hidden', 'true');
    host.style.cssText = 'position:fixed;left:-10000px;top:0;width:210mm;pointer-events:none;z-index:-1;';
    this.document.body.appendChild(host);

    let ref: ComponentRef<C> | undefined;
    try {
      // Eigen host-element ín de container: de buiten-beeld-positie mag niet mee in de snapshot.
      const sheetHost = host.appendChild(this.document.createElement('div'));
      ref = createComponent(request.component, {
        environmentInjector: this.environmentInjector,
        hostElement: sheetHost,
      });
      // Inschrijven vóór de eerste render: de SVG kan uit de cache meteen klaar zijn.
      const ready = waitForReady(ref.instance);
      for (const [name, value] of Object.entries(request.inputs)) {
        ref.setInput(name, value);
      }
      this.appRef.attachView(ref.hostView);
      ref.changeDetectorRef.detectChanges();
      await ready;
      ref.changeDetectorRef.detectChanges();
      // Sommige lagen renderen pas na een meting in een volgende frame (center-keynotes): wachten
      // tot het blad niet meer verandert, anders mist de snapshot ze.
      await waitForDomQuiet(ref.location.nativeElement as HTMLElement, () => ref?.changeDetectorRef.detectChanges());
      await this.document.fonts.ready;
      return await buildPrintSnapshot(ref.location.nativeElement as HTMLElement, { lang: request.lang, title: request.title });
    } catch (error) {
      throw error instanceof PdfPrintError ? error : new PdfPrintError('failed', error);
    } finally {
      if (ref != null) {
        this.appRef.detachView(ref.hostView);
        ref.destroy();
      }
      host.remove();
    }
  }

  private async renderPdf(html: string): Promise<Blob> {
    // Zonder EXPECTED_API_ERRORS maakt de error-interceptor van een fout een "geslaagd" antwoord
    // met de foutbody, en dan zou die als PDF gedownload worden.
    const context = new HttpContext().set(EXPECTED_API_ERRORS, true);
    try {
      return await firstValueFrom(this.printApi.postPrintPdf({ body: { html } }, context));
    } catch (error) {
      const status = error instanceof HttpErrorResponse ? error.status : undefined;
      throw new PdfPrintError(status === 503 || status === 429 ? 'busy' : 'failed', error);
    }
  }
}

/** Stil = zo lang geen DOM-wijziging; maximum zodat een blad dat blijft animeren de print niet blokkeert. */
const DOM_QUIET_MS = 150;
const DOM_QUIET_MAX_MS = 3_000;

/**
 * Wacht tot `root` een korte tijd niet meer verandert. Na elke wijziging een change detection,
 * zodat signals die door een meting (ResizeObserver, animation frame) gezet werden ook renderen.
 */
function waitForDomQuiet(root: HTMLElement, detectChanges: () => void): Promise<void> {
  return new Promise(resolve => {
    let quietTimer: ReturnType<typeof setTimeout>;
    const finish = (): void => {
      observer.disconnect();
      clearTimeout(quietTimer);
      clearTimeout(maxTimer);
      resolve();
    };
    const observer = new MutationObserver(() => {
      clearTimeout(quietTimer);
      quietTimer = setTimeout(finish, DOM_QUIET_MS);
      detectChanges();
    });
    observer.observe(root, { subtree: true, childList: true, attributes: true, characterData: true });
    const maxTimer = setTimeout(finish, DOM_QUIET_MAX_MS);
    quietTimer = setTimeout(finish, DOM_QUIET_MS);
    // Eén frame verder: de lagen die op requestAnimationFrame wachten, kunnen nu meten.
    requestAnimationFrame(() => detectChanges());
  });
}

function waitForReady(sheet: PrintSheet): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      subscription.unsubscribe();
      reject(new PdfPrintError('failed', 'printblad niet op tijd klaar'));
    }, READY_TIMEOUT_MS);
    const subscription = sheet.printReady.subscribe(ok => {
      clearTimeout(timer);
      subscription.unsubscribe();
      if (ok) resolve();
      else reject(new PdfPrintError('failed', 'printblad kon niet renderen'));
    });
  });
}
